import { TapAppStore } from '@state/tap-state';
import { TankFilterService } from '@services/tank-filter.service';
import { Injectable, OnInit } from '@angular/core';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { IFilterProfile } from '@interfaces/tankProfile';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api } from '../shared/communication-objects/api-path';
import { LogService } from './log.service';
import { Cacheable, CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';
import { ChangeFilterProfile } from '@state/actions/filter.action';
import {
  selectFilteredTanks,
  selectFilterProfile,
} from '@state/selectors/filter-profile.selectors';
import { SetLastSelectedTankQuickFilter } from '@state/actions/user-profile.actions';
import { TranslateService } from '@ngx-translate/core';
import { LocationService } from './location.service';
import {
  FilterProfileCreateNewDialogComponent,
  NewFilterProfileDialogData,
} from '@components/filter-profile-create-new-dialog/filter-profile-create-new-dialog.component';
import { selectGridFilter } from '@state/selectors/tank-profile.selectors';
import { selectUserInformation } from '@state/selectors/user-profile.selectors';
import { AppDialogService } from './app-dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { generateCopiedProfileName } from '@utils/utils';

@Injectable({ providedIn: 'root' })
export class FilterProfileService {
  private readonly apiURL = api`FilterProfile`;

  private filterProfilesCache: { data: any; timestamp: number; allowSlidingScale: boolean } = {
    data: null,
    timestamp: 0,
    allowSlidingScale: true,
  };

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private tankFilterService: TankFilterService,
    private tapAppStore: TapAppStore,
    private translate: TranslateService,
    private locationService: LocationService,
    private _appDialogService: AppDialogService
  ) {}

  @CacheBuster({ isInstant: true })
  bustCache() {
    this.filterProfilesCache = {
      data: null,
      timestamp: 0,
      allowSlidingScale: true,
    };
  }

  getFilterProfiles(organizationId: number): Observable<IFilterProfile[]> {
    return of(null).pipe(
      map(() => this.getFilterProfilesFromCache()),
      switchMap(cachedProfiles => {
        if (cachedProfiles) {
          return of(cachedProfiles);
        }
        return this.getFilterProfilesFromServer(organizationId);
      })
    );
  }

  getFilterProfile(filterProfileId: number): Observable<IFilterProfile> {
    let apiResponse = this.restClient.getDirect<IFilterProfile>(
      api`${this.apiURL}/GetFilterProfile/${filterProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('GetFilterProfile Response', response))
    );
  }

  getFilterProfileGlobally(filterProfileId: number): Observable<any> {
    let apiResponse = this.restClient.get<IFilterProfile[]>(
      api`${this.apiURL}/GetFilterProfileGlobally/${filterProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('GetFilterProfile Response', response))
    );
  }

  copyFilterProfile(profile: IFilterProfile): Observable<IFilterProfile> {
    return this._generateCopiedProfileName(profile.filterProfileName).pipe(
      switchMap(newProfileName => {
        if (!newProfileName) return EMPTY;

        const payload = {
          filterProfileId: profile.filterProfileId,
          userId: profile.userId,
          newFilterProfileName: newProfileName,
        };
        this.bustCache();
        let apiResponse = this.restClient.postForResponse<IFilterProfile>(
          api`${this.apiURL}/copy-filter-profile`,
          payload
        );
        this._appDialogService.showGeneralMessage(
          this.translate.instant('TankProfile.CopingFilterProfile')
        );
        return apiResponse.pipe(
          tap({
            next: filterProfile => {
              this.logger.log('Filter Profile Copied', filterProfile);
              this._appDialogService.showSuccessMessage(
                this.translate.instant('TankGridHeader.CreateFilterProfileSuccess')
              );
            },
            error: error => {
              this.logger.error('Error creating filter profile', error);
              this._appDialogService.showErrorMessage(
                this.translate.instant('TankGridHeader.CreateFilterProfileFailed')
              );
            },
          })
        );
      })
    );
  }

  createFilterProfile(
    currentFilterProfile: IFilterProfile,
    cloneGridFilterJson: boolean = true
  ): Observable<IFilterProfile> {
    let profileName = currentFilterProfile?.filterProfileName ?? 'New Filter';
    return this._generateCopiedProfileName(profileName).pipe(
      switchMap(newProfileName =>
        this.getFilterProfileCreateNewDialog(newProfileName).afterClosed()
      ),
      switchMap(data => {
        if (data=== null) return of(null);

        // disallow duplicate profile names
        return this.tapAppStore
          .getCsoId$()
          .pipe(switchMap(csoId => this.getFilterProfiles(csoId)))
          .pipe(
            take(1),
            switchMap((profiles: IFilterProfile[]) => {
              let foundProfile: IFilterProfile = null;
              foundProfile = profiles?.find(
                profile => profile.filterProfileName === data.profileName
              );

              if (foundProfile) {
                // duplicate profile found
                this._appDialogService.showErrorMessage(
                  this.translate.instant('TankGridHeader.DuplicateFilterProfileName')
                );
                return of(null);
              }

              return combineLatest([
                this.tapAppStore.store.select(selectGridFilter),
                this.tapAppStore.store.select(selectUserInformation),
                this.tapAppStore.store.select(selectFilteredTanks),
              ]).pipe(
                take(1),
                switchMap(([gridRequest, userInfo, filteredTanks]) => {
                  let filterProfile: IFilterProfile = {
                    active: true,
                    filterProfileId: 0,
                    filterProfileName: data?.profileName,
                    organizationId: userInfo?.lastSelectedOrganizationId,
                    syncStatus: -1,
                    tankFilterJson: data?.tankFilterJson ?? null,
                    tankProfileId: userInfo?.lastSelectedTankProfileId,
                    userId: userInfo?.userId,
                    isPrivate: true, //default to true
                    organizationName: userInfo?.lastSelectedOrganizationName,
                    owner: userInfo?.firstName + ' ' + userInfo?.lastName,
                    filteredTanks: data?.filteredTanks ?? [],
                    isPersonal: true, //default to true
                  };

                  if (cloneGridFilterJson) {
                    filterProfile.tankFilterJson = JSON.stringify(gridRequest.filterModel);
                    filterProfile.filteredTanks = filteredTanks;
                  }
                  this.bustCache();
                  this.warnUserIfSkytankFilterActive();
                  return this.createFilterProfileOnServer(filterProfile).pipe(
                    take(1),
                    tap({
                      next: () => {
                        this.logger.log('Filter Profile Created', filterProfile);
                        this._appDialogService.showSuccessMessage(
                          this.translate.instant('TankGridHeader.CreateFilterProfileSuccess')
                        );
                      },
                      error: error => {
                        this.logger.error('Error creating filter profile', error);
                        this._appDialogService.showErrorMessage(
                          this.translate.instant('TankGridHeader.CreateFilterProfileFailed')
                        );
                      },
                    })
                  );
                })
              );
            })
          );
      })
    );
  }

  getFilterProfileCreateNewDialog(
    profileName: string
  ): MatDialogRef<FilterProfileCreateNewDialogComponent, any> {
    let dialogData: NewFilterProfileDialogData = {
      profileName: profileName,
    };

    return this._appDialogService.showDialogReturnRef(
      FilterProfileCreateNewDialogComponent,
      dialogData
    );
  }

  filterProfileNameAlreadyExists(
    filterProfileName: string,
    filterProfileId: number,
    orgId: number
  ): Observable<boolean> {
    const fpnl = filterProfileName?.trim()?.toLowerCase();
    return this.getFilterProfiles(orgId).pipe(
      map(profiles =>
        profiles.some(
          profile =>
            profile.filterProfileName?.trim()?.toLowerCase() === fpnl &&
            profile.filterProfileId !== filterProfileId
        )
      ),
      tap(result => this.logger.log('Filter Profile Name Already Exists', result))
    );
  }

  warnUserIfSkytankFilterActive() {
    //test if skytank filter is active and if so warn the user Skytank filters are not persisted.
    this.tapAppStore.store.pipe(take(1)).subscribe(state => {
      if(state?.gridFilter['includeAllGateways'] === true){ {
        this.showSkytankFilterWarning();
      }
    }
    });
  }

  showSkytankFilterWarning(){
    const msg = this.translate.instant('FilterProfile.SkytankFilterNotSupportedWarning') || 'Skytank Filter Not Supported';
    const title = this.translate.instant('FilterProfile.SkytankFilterNotSupportedWarningTitle') || 'Filter Profile Warning';
    this._appDialogService.showGeneralDialog(
      title,
      msg,
    );
  }
   
  saveFilterProfile(filterProfile: IFilterProfile) {
    this.bustCache();
    
    //Handle Skytank Filter - Skytank filter is not supported, so we need to remove it from the filter profile before saving. We also want to show a warning message to the user if they have a skytank filter in their profile that it will be removed upon saving.
    filterProfile = this.removeSkytankFilterAndWarnUser(filterProfile);
    let apiResponse = this.restClient.post(api`${this.apiURL}/SaveFilterProfile`, filterProfile);
    return apiResponse.pipe(
      tap(response => this.logger.log('SaveFilterProfile Response', response))
    );
  }

  deleteFilterProfile(profile: IFilterProfile): Observable<boolean> {
    return this._appDialogService
      .showConfirmationDialog(
        this.translate.instant('FilterProfile.DeleteConfirmationTitle') || 'Delete Filter Profile',
        this.translate.instant('FilterProfile.DeleteConfirmationMessage', {
          name: profile.filterProfileName,
        }) || `Are you sure you want to delete '${profile.filterProfileName}'?`,
        this.translate.instant('General.Delete') || 'Delete',
        this.translate.instant('General.Cancel') || 'Cancel'
      )
      .pipe(
        switchMap(confirmed => {
          if (confirmed) {
            return this.filterProfileHasDependents(profile.filterProfileId).pipe(
              take(1),
              switchMap(hasDependents => {
                if (hasDependents) {
                  this.showFilterProfileHasDependentsError();
                  return of(false);
                } else {
                  this._appDialogService.showGeneralMessage(
                    this.translate.instant('FilterProfile.DeletingFilterProfile')
                  );
                  this.bustCache();
                  return this.deleteFilterProfileFromServer(profile.filterProfileId).pipe(
                    take(1),
                    tap({
                      next: () => {
                        this._appDialogService.showSuccessMessage(
                          this.translate.instant('FilterProfile.FilterProfileDeletedSuccess')
                        );
                        this.logger.log('Filter Profile Deleted', profile);
                      },
                      error: error => {
                        this.logger.error('Error deleting filter profile', error);
                        this._appDialogService.showErrorMessage(
                          this.translate.instant('FilterProfile.FilterProfileDeletedFailed')
                        );
                      },
                    })
                  );
                }
              })
            );
          }
        })
      );
  }

  /*
   * STATE Store Methods
   */

  clearLastSelectedFilterProfile() {
    // clear the currently last selected filter profile
    this.tapAppStore.store.dispatch(new ChangeFilterProfile(null));

    //set default Tank Quick Filter - Status Bar Tank Filters
    this.tankFilterService.resetLastSelectedTankQuickFilter();
  }

  getLastSelectedFilterProfile(): Observable<IFilterProfile> {
    return this.tapAppStore.store.select(selectFilterProfile);
  }

  setLastSelectedFilterProfile(filterProfile: IFilterProfile) {
    //change the last selected quick filter to null when setting a filter profile.
    //This is to prevent the last selected quick filter from being applied when the filter profile is applied.
    this.tapAppStore.store.dispatch(new SetLastSelectedTankQuickFilter(null));
    this.tapAppStore.store.dispatch(new ChangeFilterProfile(filterProfile));
    let fm = JSON.parse(filterProfile?.tankFilterJson ?? '{}');

    // if we have a geolocation filter in play that is based on a location id, we need to update the lat/lon as this can change elsewhere
    if (fm?.geoLocationFilter != null && fm?.geoLocationFilter.values.length >= 2) {
      const locationId = +fm?.geoLocationFilter.values[0];
      if (locationId > 0) {
        //get the latest lat/lon
        this.locationService.getLocationLatLon(locationId).subscribe(
          (data: any) => {
            // re-build the filter model with the new lat/lon
            const distance = +fm?.geoLocationFilter.values[1];
            const centerLat: number = data.latitude;
            const centerLon: number = data.longitude;
            let filterModel = JSON.parse(filterProfile?.tankFilterJson ?? '{}');
            filterModel.geoLocationFilter.values = [
              locationId.toString(),
              distance.toString(),
              centerLat.toString(),
              centerLon.toString(),
            ];
            this.tankFilterService.applyFilterModelToState(filterModel, this.constructor.name);
            this._appDialogService.showSuccessMessage(
              this.translate.instant('FilterProfiles.FilterProfileAppliedSuccess')
            );
            return;
          },
          error =>
            this.logger.error('An error occured - ' + error.status + ' - ' + error.statusText)
        );
      }
    }
    else{
      this.tankFilterService.applyFilterModelToState(fm, this.constructor.name);
      this._appDialogService.showSuccessMessage(
                this.translate.instant('FilterProfiles.FilterProfileAppliedSuccess')
      );
    }
  }

  filterProfileHasDependents(filterProfileId: number): Observable<boolean> {
    const apiResponse = this.restClient.getDirect<boolean>(
      api`${this.apiURL}/filter-profile-has-dependents/${filterProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('Filter Profile Has Dependents', response)),
      map(response => response)
    );
  }

  showFilterProfileHasDependentsError() {
    this._appDialogService.showErrorMessage(
      this.translate.instant('FilterProfile.Form.FilterProfileHasDependents') ||
        'The Filter Profile is being used by other and can’t be modified to private or deleted at this moment.'
    );
  }

  private createFilterProfileOnServer(filterProfile: IFilterProfile): Observable<IFilterProfile> {
    globalCacheBusterNotifier.next();
    //Handle Skytank Filter - Skytank filter is not supported, so we need to remove it from the filter profile before saving. We also want to show a warning message to the user if they have a skytank filter in their profile that it will be removed upon saving.
    filterProfile = this.removeSkytankFilterAndWarnUser(filterProfile);
    let apiResponse = this.restClient.post(api`${this.apiURL}/CreateFilterProfile`, filterProfile);
    return apiResponse.pipe(
      tap(response => this.logger.log('CreateFilterProfile Response', response)),
      tap(response => {
        if (!response || !response.filterProfileId) {
          this.logger.error('CreateFilterProfile failed, no filterProfileId returned', response);
          this._appDialogService.showErrorMessage(
            this.translate.instant('TankGridHeader.CreateFilterProfileFailed')
          );
          return;
        }
        //set filterProfileID
        filterProfile.filterProfileId = response.filterProfileId;
        this.setLastSelectedFilterProfile(filterProfile);
        this.logger.log('Filter Profile Created', filterProfile);
      })
    );
  }

  private removeSkytankFilterAndWarnUser(filterProfile: IFilterProfile) {
    this.showSkytankFilterWarning();

    let filterModel = JSON.parse(filterProfile?.tankFilterJson ?? '{}');
    if (filterModel?.skytankFilter) {
      delete filterModel.skytankFilter;
      filterProfile = { ...filterProfile, tankFilterJson: JSON.stringify(filterModel) };
    }
    return filterProfile;
  }

  private deleteFilterProfileFromServer(filterProfileId: number) {
    const apiResponse = this.restClient.post(api`${this.apiURL}/DeleteFilterProfile`, {
      filterProfileId,
    });
    return apiResponse.pipe(
      tap(response => this.logger.log('DeleteFilterProfile Response', response))
    );
  }

  private _generateCopiedProfileName(profileName: string) {
    return this.tapAppStore.getCsoId$().pipe(
      switchMap(csoId => this.getFilterProfiles(csoId)),
      map(profiles => {
        return generateCopiedProfileName(
          profileName,
          profiles.map(p => p.filterProfileName)
        );
      })
    );
  }

  private getFilterProfilesFromCache(): IFilterProfile[] | null {
    if (this.filterProfilesCache.data) {
      const currentTime = Date.now();
      if (currentTime - this.filterProfilesCache.timestamp < 5000) {
        // 5 seconds cache
        if (this.filterProfilesCache.allowSlidingScale) {
          this.filterProfilesCache.timestamp = currentTime;
        }
        return this.filterProfilesCache.data;
      }
      return null; // Cache expired
    }
  }

  private getFilterProfilesFromServer(organizationId: number): Observable<IFilterProfile[]> {
    let apiResponse = this.restClient.getDirect<IFilterProfile[]>(
      api`${this.apiURL}/GetUserFilterProfiles/${organizationId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('GetUserFilterProfiles Response', response)),
      map((profiles: IFilterProfile[]) => {
        this.filterProfilesCache.data = profiles;
        this.filterProfilesCache.timestamp = Date.now();
        return profiles;
      })
    );
  }
}
